import {
  BrainCircuit,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Crosshair,
  Database,
  FileBarChart2,
  FlaskConical,
  Hash,
  Headset,
  Home,
  LifeBuoy,
  LogOut,
  MapPin,
  Megaphone,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  UserCheck,
  UserCog,
  Users,
  X,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router';
import { useState } from 'react';
import { useAuth, usePermissions } from '@/hooks';
import { cn } from '@/utils';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  supervisorOnly?: boolean;
  agentOnly?: boolean;
  adminOnly?: boolean;
}

// Phase 1 — Address Verification
const ADDRESS_VERIFICATION_ITEMS: NavItem[] = [
  { to: '/assignments', label: 'My Batches', icon: ClipboardList, agentOnly: true },
  { to: '/beneficiaries', label: 'Beneficiaries', icon: Users,adminOnly: true  },
  { to: '/batches', label: 'Data Batches', icon: Database, supervisorOnly: true },
  { to: '/admin/reprocess-batches', label: 'Reprocess Batches', icon: RefreshCw, adminOnly: true },
];

// Phase 2 — Appointments
// Cluster Assignments/Planning/Visit Planning are superseded by District Assignments + Camps
// Planning (agents/managers are scoped to districts, which own the camps) and are hidden from nav.
const APPOINTMENTS_SUPERVISOR_ITEMS: NavItem[] = [
  { to: '/all-camps', label: 'Camp-wise Beneficiaries', icon: MapPin, adminOnly: true },
];

// Planning — admin/supervisor only
const PLANNING_ITEMS: NavItem[] = [
  { to: '/planning/camps', label: 'Camps Planning', icon: FlaskConical, supervisorOnly: true },
  { to: '/planning/manual-camps', label: 'Radius-Based Camp Planning', icon: Crosshair, supervisorOnly: true },
  { to: '/planning/agentic', label: 'Planning by Agentic AI', icon: Sparkles, supervisorOnly: true },
  { to: '/planning/engine', label: 'Camp Generation Engine', icon: BrainCircuit, supervisorOnly: true },
  { to: '/planning/pincode-camps', label: 'Pincode Camps', icon: Hash, supervisorOnly: true },
];

const APPOINTMENTS_AGENT_ITEMS: NavItem[] = [
  { to: '/my-camps', label: 'My Camps', icon: MapPin, agentOnly: true },
];

// Visible to all authenticated users
const APPOINTMENTS_COMMON_ITEMS: NavItem[] = [
  { to: '/appointments', label: 'Appointments', icon: CalendarDays },
];

// Support — admin only (pincode validation, etc.)
const SUPPORT_ITEMS: NavItem[] = [
  { to: '/support/pincode-validation', label: 'Pincode Validation', icon: Crosshair, adminOnly: true },
  { to: '/support/pincode-geocoding', label: 'Pincode Geocoding', icon: MapPin, adminOnly: true },
  { to: '/support/address-geocoding', label: 'Address Geocoding', icon: Crosshair, adminOnly: true },
  { to: '/support/location-geocoding', label: 'Location Geocoding', icon: MapPin, adminOnly: true },
  { to: '/support/beneficiary-generation', label: 'Beneficiary Generation', icon: Users, adminOnly: true },
  { to: '/support/manual-camp-mapping', label: 'Manual Camp Mapping', icon: RefreshCw, adminOnly: true },
];

// Admin — admin only
const ADMIN_ITEMS: NavItem[] = [
  { to: '/admin/users', label: 'Manage Users', icon: UserCog, adminOnly: true },
  { to: '/admin/campaigns', label: 'Assign Campaigns', icon: Megaphone, adminOnly: true },
  { to: '/admin/labs', label: 'Assign Labs', icon: FlaskConical, adminOnly: true },
  { to: '/district-assignments', label: 'District Assignments', icon: ShieldCheck, adminOnly: true },
  { to: '/camps', label: 'Camps', icon: MapPin, adminOnly: true },
  { to: '/admin/reports', label: 'Reports', icon: FileBarChart2, adminOnly: true },
];

// Phase 3 — Other (kept disabled until ready)
const OTHER_NAV_ITEMS: NavItem[] = [
  { to: '/communications', label: 'Communications', icon: MessageSquare },
  { to: '/support', label: 'Support', icon: LifeBuoy },
];

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

function NavItemLink({ item, onClose }: { item: NavItem; onClose: () => void }): React.JSX.Element {
  const { to, label, icon: Icon } = item;
  return (
    <NavLink
      key={to}
      to={to}
      onClick={onClose}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive ? 'bg-sidebar-active text-white' : 'text-sidebar-foreground/85 hover:bg-white/10 hover:text-white',
        )
      }
    >
      <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
      {label}
    </NavLink>
  );
}

interface CollapsibleSectionProps {
  label: string;
  icon: React.ElementType;
  items: NavItem[];
  isActive: boolean;
  onClose: () => void;
}

function CollapsibleSection({ label, icon: Icon, items, isActive, onClose }: CollapsibleSectionProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(isActive);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive ? 'bg-sidebar-active text-white' : 'text-sidebar-foreground/85 hover:bg-white/10 hover:text-white',
        )}
      >
        <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
        <span className="flex-1 text-left">{label}</span>
        {expanded ? (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
      {expanded && (
        <div className="mt-0.5 space-y-0.5 pl-6">
          {items.map((item) => (
            <NavItemLink key={item.to} item={item} onClose={onClose} />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ mobileOpen, onClose }: SidebarProps): React.JSX.Element {
  const { logout } = useAuth();
  const { canManageBatches, role } = usePermissions();
  const location = useLocation();

  const isDisabled = (item: NavItem): boolean => {
    if (item.adminOnly && role !== 'admin') return true;
    if (item.supervisorOnly && !canManageBatches) return true;
    if (item.agentOnly && role !== 'agent') return true;
    return false;
  };

  const filteredAddressItems = ADDRESS_VERIFICATION_ITEMS.filter((item) => !isDisabled(item));

  // Appointments items: supervisors see planning/assignments, agents see buckets, all see camps+appointments
  const filteredAppointmentItems = [
    ...APPOINTMENTS_SUPERVISOR_ITEMS.filter((item) => !isDisabled(item)),
    ...APPOINTMENTS_AGENT_ITEMS.filter((item) => !isDisabled(item)),
    ...APPOINTMENTS_COMMON_ITEMS,
  ];

  const isAddressActive = filteredAddressItems.some(
    (item) => location.pathname === item.to || location.pathname.startsWith(item.to + '/'),
  );

  const isAppointmentsActive = filteredAppointmentItems.some(
    (item) => location.pathname === item.to || location.pathname.startsWith(item.to + '/'),
  );

  const filteredPlanningItems = PLANNING_ITEMS.filter((item) => !isDisabled(item));
  const isPlanningActive = filteredPlanningItems.some(
    (item) => location.pathname === item.to || location.pathname.startsWith(item.to + '/'),
  );

  return (
    <>
      {mobileOpen ? (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} aria-hidden="true" />
      ) : null}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-screen w-64 flex-col overflow-hidden bg-sidebar text-sidebar-foreground transition-transform lg:static lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        <div className="flex items-center gap-3 px-5 py-6">
          <span className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white/70">
            <Headset className="h-5 w-5 text-white" aria-hidden="true" />
          </span>
          <span className="text-lg font-bold leading-tight text-white">
            Health Camp
            <br />
            Call Center
          </span>
          <button
            type="button"
            className="ml-auto rounded p-1 hover:bg-white/10 lg:hidden"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-2">
          {/* Home */}
          <div>
            <NavLink
              to="/"
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive ? 'bg-sidebar-active text-white' : 'text-sidebar-foreground/85 hover:bg-white/10 hover:text-white',
                )
              }
            >
              <Home className="h-[18px] w-[18px]" aria-hidden="true" />
              Home
            </NavLink>
          </div>

          {/* Phase 1 — Address Verification */}
          {filteredAddressItems.length > 0 && (
            <CollapsibleSection
              label="Address Verification"
              icon={UserCheck}
              items={filteredAddressItems}
              isActive={isAddressActive}
              onClose={onClose}
            />
          )}

          {/* Phase 2 — Appointments */}
          {filteredAppointmentItems.length > 0 && (
            <CollapsibleSection
              label="Appointments"
              icon={CalendarDays}
              items={filteredAppointmentItems}
              isActive={isAppointmentsActive}
              onClose={onClose}
            />
          )}

          {/* Planning — admin/supervisor only */}
          {filteredPlanningItems.length > 0 && (
            <CollapsibleSection
              label="Planning"
              icon={FlaskConical}
              items={filteredPlanningItems}
              isActive={isPlanningActive}
              onClose={onClose}
            />
          )}

          {/* Reports — admin/supervisor only */}
          {/* {REPORTS_NAV_ITEMS.filter((item) => !isDisabled(item)).length > 0 && (
            <div>
              {REPORTS_NAV_ITEMS.filter((item) => !isDisabled(item)).map((item) => (
                <NavItemLink key={item.to} item={item} onClose={onClose} />
              ))}
            </div>
          )} */}

          {/* Support — admin only */}
          {SUPPORT_ITEMS.filter((item) => !isDisabled(item)).length > 0 && (
            <CollapsibleSection
              label="Support"
              icon={LifeBuoy}
              items={SUPPORT_ITEMS.filter((item) => !isDisabled(item))}
              isActive={SUPPORT_ITEMS.some(
                (item) => location.pathname === item.to || location.pathname.startsWith(item.to + '/'),
              )}
              onClose={onClose}
            />
          )}

          {/* Admin — admin only */}
          {ADMIN_ITEMS.filter((item) => !isDisabled(item)).length > 0 && (
            <CollapsibleSection
              label="Admin"
              icon={ShieldCheck}
              items={ADMIN_ITEMS.filter((item) => !isDisabled(item))}
              isActive={ADMIN_ITEMS.some(
                (item) => location.pathname === item.to || location.pathname.startsWith(item.to + '/'),
              )}
              onClose={onClose}
            />
          )}

          {/* Phase 3 — Other (disabled until ready) */}
          <div>
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/60">
              Other
            </p>
            <div className="space-y-0.5 opacity-40">
              {OTHER_NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <span
                    key={item.to}
                    className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/85"
                    title="Coming soon"
                  >
                    <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                    {item.label}
                  </span>
                );
              })}
            </div>
          </div>
        </nav>

        <div className="border-t border-white/10 px-3 py-4">
          <button
            type="button"
            data-blocked-during-call
            onClick={() => void logout()}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-sidebar-foreground/85 transition-colors hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
            Logout
          </button>
        </div>
      </aside>
    </>
  );
}
